import { useEffect, useState } from 'react'

import { useCreateBooking } from '@/api/models/parking/hooks/useCreateBooking'
import {
  CreateBookingFormData,
  ParkingSummary,
  TariffType
} from '@/api/models/parking/lib/parking.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { FixedBottomDrawerButton } from '@/components/entities/user/ui/FixedBottomDrawerButton'
import { AppButton } from '@/components/shared/button'
import { DateRange } from '@/components/shared/datepicker/ui/Calendar.types'
import { getDisplayRangeEnd } from '@/components/shared/datepicker/utils/getDisplayRangeEnd'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'
import {
  DAY_WORDS,
  HOUR_WORDS,
  MINUTE_WORDS,
  pluralization
} from '@/utils/string-formatting'

import { getDailyPeriodEnd, getDailyPeriodStart } from '../lib/dates'
import { SummaryBlock } from './SummaryBlock'

type Props = {
  formData: CreateBookingFormData
  data: ParkingSummary
  date: DateRange
  tariffType: TariffType
}

const formatDuration = (durationSeconds: number, tariffType: TariffType) => {
  if (tariffType === TariffType.Daily) {
    const days = Math.round(durationSeconds / 86_400)

    return `${days} ${pluralization(days, DAY_WORDS)}`
  }

  const totalHours = Math.floor(durationSeconds / 3600)
  const minutes = Math.floor(durationSeconds / 60) - totalHours * 60

  return `${totalHours} ч. ${String(minutes).padStart(2, '0')} м.`
}

const formatFreePeriod = (freeSeconds: number) => {
  if (freeSeconds <= 0) {
    return null
  }

  const isWholeHours = freeSeconds % 3600 === 0
  const value = isWholeHours ? freeSeconds / 3600 : Math.round(freeSeconds / 60)
  const unitWords = isWholeHours ? HOUR_WORDS : MINUTE_WORDS

  if (value === 1) {
    return `${isWholeHours ? 'Первый' : 'Первая'} ${unitWords[0]}`
  }

  return `Первые ${value} ${pluralization(value, unitWords)}`
}

export const Summary = ({ formData, data, date, tariffType }: Props) => {
  const { reachGoal } = useYandexMetrika()

  const { personalAccountId } = formData
  const freePeriodLabel = formatFreePeriod(data.freeSeconds)

  const { mutateAsync: createBooking, isPending } = useCreateBooking(formData)
  const { navigateToPaymentFromBooking } = useRequestNavigation()

  const [createdInvoiceId, setCreatedInvoiceId] = useState<string | undefined>()
  const [createdBookingId, setCreatedBookingId] = useState<string | undefined>()

  const onBookingCreate = async () => {
    const response = await createBooking()

    if (response.data?.invoiceId) {
      setCreatedInvoiceId(response.data.invoiceId)
    }

    if (response.data?.bookingId) {
      setCreatedBookingId(response.data.bookingId)
    }

    reachGoal(YandexMetrikaEvent.PASSES_RENT_PAY)
  }

  useEffect(() => {
    if (createdBookingId && createdInvoiceId && personalAccountId) {
      navigateToPaymentFromBooking({
        bookingId: createdBookingId,
        invoiceId: createdInvoiceId,
        personalAccountId
      })
    }
  }, [
    createdBookingId,
    createdInvoiceId,
    navigateToPaymentFromBooking,
    personalAccountId
  ])

  return (
    <div className="mb-[120px] mt-16 overflow-y-auto pb-40 lg:pb-24 sm:pb-20 xxs:mt-0">
      <p className="mb-40 text-grey-80 text-24 xxs:mb-20 xxs:text-14">
        Проверьте время начала и окончания аренды
        {data.offerUrl && (
          <>
            {' '}
            и ознакомьтесь с{' '}
            <a
              className="text-bronze-40"
              href={data.offerUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              публичной офертой
            </a>
          </>
        )}
      </p>
      <div className="flex items-center justify-center xs:flex-col">
        {date.from && (
          <SummaryBlock
            label="Начало"
            date={
              tariffType === TariffType.Daily
                ? getDailyPeriodStart(date.from)
                : date.from
            }
          />
        )}
        <hr className="h-[2px] w-[72px] text-grey-100/10 xs:m-8 xs:w-16 xs:rotate-90" />
        <div className="flex h-[164px] w-[164px] flex-col items-center justify-center rounded-16 bg-bronze-10 xs:h-[99px] xs:w-full">
          <strong className="text-28">
            {formatDuration(data.durationSeconds, tariffType)}
          </strong>
          <p className="text-grey-80 text-16">
            {data.tariff} ₽
            {`/${tariffType === TariffType.Daily ? 'день' : 'час'}`}
          </p>
        </div>
        <hr className="h-[2px] w-[72px] text-grey-100/10 xs:m-8 xs:w-16 xs:rotate-90" />
        {date.to && (
          <SummaryBlock
            label="Окончание"
            date={
              tariffType === TariffType.Daily
                ? getDailyPeriodEnd(date.to)
                : getDisplayRangeEnd(date.to)
            }
          />
        )}
      </div>
      <FixedBottomDrawerButton
        topContent={
          freePeriodLabel && (
            <p className="text-grey-80 text-24 xxs:text-14">
              {freePeriodLabel} аренда бесплатная
            </p>
          )
        }
      >
        <div className="flex w-full items-center justify-between xs:flex-col">
          <div className="flex flex-col xs:w-full xs:flex-row xs:items-center xs:justify-between xs:gap-12">
            <strong className="text-18 xs:text-16">Сумма</strong>
            <strong className="text-36 xs:text-28">{data.totalSum} ₽</strong>
          </div>
          <AppButton
            className="!h-[56px] w-[200px] xs:!h-[44px] xs:w-full"
            onClick={onBookingCreate}
            loading={isPending}
          >
            Оплатить
          </AppButton>
        </div>
      </FixedBottomDrawerButton>
    </div>
  )
}
